//
import { getDefaultState } from './state';
import * as getters from './getters';
import { defineStore, type StoreDefinition } from 'pinia';
import { mapPolicyResponse } from '../models/Policy';
import { mapPaymentInfoResponse, mapPayNowInfoResponse } from '../models/PaymentInfo';
import type {
  IAddChildRiderPayload,
  IGetBankDataPayload,
  IGetBeneficiariesResponse,
  IGetPaymentInfoResponse,
  IPolicy,
} from '@/shared/api/models.bff';
import bff from '@/shared/api/bff';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { checkKubraPayment } from '@/shared/util/kubra';
import type {
  IContactInfo,
  IPolicyStoreBeneficiaryInfo,
  IPolicyStorePaymentInfoWBankAccount,
  IPolicyStorePaymentPanelItem,
  IPolicyStorePolicy,
  IPolicyStorePolicyOwner,
} from './policyStore.model';
import type { IGlobalError } from '@/stores/global/globalStore.model.ts';

const STEP = {
  READY: 0,
  ADD_A_CHILD: 50,
};

export type PaymentSelectionType = 'TOTAL' | 'MINIMUM';

export const usePolicyStore: StoreDefinition = defineStore('policy', {
  state: getDefaultState,
  getters,
  actions: {
    setPolicies(policies: IPolicy[]) {
      // ! see mapPolicyResponse for mapping
      this.policies = policies.map((policy) => mapPolicyResponse(policy));
    },
    // setSelectedPolicyIndex(selectedPolicyIndex) {
    //   this.selectedPolicyIndex = selectedPolicyIndex; // TODO: access state directly
    // },
    // TODO: verify if used
    setPanelValue(panelValue: number) {
      const paymentPanel = this.paymentPanel[this.selectedPolicyIndex];
      paymentPanel.panelValue = panelValue;
    },
    // TODO: verify if used
    setContactDetailsPanel(panelValue: number) {
      this.contactDetailsPanel = panelValue;
    },
    // TODO: verify if used
    setContactInfoEdit(editContactInfo: boolean) {
      this.editContactInfo = editContactInfo;
    },
    setPolicyOwnerAddressIfUndefined() {
      const policy = this.policies[this.selectedPolicyIndex];
      if (!policy.owner) {
        const owner: IPolicyStorePolicyOwner = {
          name: '',
          dob: null,
          ssn: '',
          email: '',
          address: {
            phone: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
          },
        };
        policy.owner = owner;
      }
    },
    updateOwnerAddress1(value: string = '') {
      const policy = this.policies[this.selectedPolicyIndex];
      this.setPolicyOwnerAddressIfUndefined();
      policy.owner!.address.address1 = value;
    },
    updateOwnerAddress2(value: string = '') {
      const policy = this.policies[this.selectedPolicyIndex];
      this.setPolicyOwnerAddressIfUndefined();
      policy.owner!.address.address2 = value;
    },
    updateOwnerCity(value: string = '') {
      const policy = this.policies[this.selectedPolicyIndex];
      this.setPolicyOwnerAddressIfUndefined();
      policy.owner!.address.city = value;
    },
    updateOwnerState(value: string = '') {
      const policy = this.policies[this.selectedPolicyIndex];
      this.setPolicyOwnerAddressIfUndefined();
      policy.owner!.address.state = value;
    },
    updateOwnerZip(value: string = '') {
      const policy = this.policies[this.selectedPolicyIndex];
      this.setPolicyOwnerAddressIfUndefined();
      policy.owner!.address.zip = value;
    },
    updateOwnerPhone(value: string = '') {
      const policy = this.policies[this.selectedPolicyIndex];
      this.setPolicyOwnerAddressIfUndefined();
      policy.owner!.address.phone = value;
    },
    updateOwnerEmail(value: string = '') {
      const policy = this.policies[this.selectedPolicyIndex];
      this.setPolicyOwnerAddressIfUndefined();
      policy.owner!.email = value;
    },
    // TODO: verify if used; access state directly
    // setSuccessResponseUpdateContactInfo(value) {
    //   this.successResponseUpdateContactInfo = value;
    // },
    // TODO: verify if used; access state directly
    // setErrorResponseUpdateContactInfo(value) {
    //   this.errorResponseUpdateContactInfo = value;
    // },
    // TODO: verify if used; access state directly
    // setAddressVerification(value) {
    //   this.addressVerification = value;
    // },
    // TODO: verify if used; access state directly
    // setAddressUpdateSuccess(value) {
    //   this.addressUpdateSuccess = value;
    // },
    // TODO: verify if used; access state directly
    // setNavHintDismissed(value) {
    //   this.navHintDismissed = value;
    // },
    // TODO: verify if used; access state directly
    // setHasTerminatedPolicies(value) {
    //   this.hasTerminatedPolicies = value;
    // },
    // TODO: verify if used; access state directly
    // setTerminatedPolicyAgent(agent) {
    //   this.terminatedPolicyAgent = agent;
    // },
    // TODO: verify if used
    setPaymentInfoLoading(loading: boolean) {
      this.loading.payment = loading;
    },
    // TODO: verify if used
    setAchSubmissionLoading(loading: boolean) {
      this.loading.ach = loading;
    },
    // TODO: verify if used
    setPolicyInfoLoading(loading: boolean) {
      this.loading.policy = loading;
    },

    setPaymentInfo(paymentInfo: IGetPaymentInfoResponse) {
      // Do some mapping
      // from IGetPaymentInfoResponse >>> IPolicyStorePolicyPaymentInfo
      // from IGetPaymentInfoResponse >>> IPolicyStorePolicyPayNowInfo
      const policy = this.policies[this.selectedPolicyIndex];
      policy.paymentInfo = mapPaymentInfoResponse(paymentInfo);
      policy.paynowInfo = mapPayNowInfoResponse(paymentInfo);
    },
    // TODO: verify if used; access state directly
    // setBase64String(base64String) {
    //   this.base64String = base64String;
    // },
    // TODO: verify if used; access state directly
    // setBankName(bankName) {
    //   this.bankName = bankName;
    // },
    // TODO: verify if used; access state directly
    // clearBank(state) {
    //   this.bankName = null;
    // },
    // TODO: verify if used; access state directly
    // setPaymentData(paymentData) {
    //   this.paymentData = paymentData;
    // },
    // TODO: verify if used
    /* Edit payment details */
    // TODO: verify if used
    setPaymentPanel(policies: IPolicyStorePolicy[]) {
      this.paymentPanel = [];
      policies.forEach((policy) => {
        const eachPanel = {
          contractId: Number(policy.contractId),
          editPayments: {
            paymentMethodChangeAlert: false,
            editPaymentMethod: false,
            disablePaymentDetailsLink: false,
          },
          panelValue: -1,
        } as IPolicyStorePaymentPanelItem;
        this.paymentPanel.push(eachPanel);
      });
    },
    // TODO: verify if used
    setPaymentMethodChangeAlert(paymentMethodChangeAlert: boolean) {
      const paymentPanel = this.paymentPanel[this.selectedPolicyIndex];
      paymentPanel.editPayments.paymentMethodChangeAlert = paymentMethodChangeAlert;
    },
    // TODO: verify if used
    setEditPaymentMethod(editPaymentMethod: boolean) {
      const paymentPanel = this.paymentPanel[this.selectedPolicyIndex];
      paymentPanel.editPayments.editPaymentMethod = editPaymentMethod;
    },
    // TODO: verify if used; access state directly
    setDisablePaymentDetailsLink(disablePaymentDetailsLink: boolean) {
      const paymentPanel = this.paymentPanel[this.selectedPolicyIndex];
      paymentPanel.editPayments.disablePaymentDetailsLink = disablePaymentDetailsLink;
    },
    // TODO: verify if used; access state directly
    // setshowPayNowInfoCard(value) {
    //   this.showPayNowInfoCard = value;
    // },
    // TODO: verify if used; access state directly
    // setPaymentInfoShowPaymentAmountDateBillingMethod(value) {
    //   this.paymentInfoShowPaymentAmountDateBillingMethod = value;
    // },
    // TODO: verify if used; access state directly
    // setUpdatingBank(value) {
    //   this.updatingBank = value;
    // },
    // TODO: verify if used; access state directly
    // setpayNowLinkToggle(value) {
    //   this.payNowLinkToggle = value;
    // },
    // TODO: verify if used; access state directly
    // setPreLoadBank(value) {
    //   this.preLoadBank = value;
    // },
    // TODO: verify if used; access state directly
    // setBeneficiaries(value) {
    //   this.beneficiaries = value;
    // },
    // TODO: verify if used; access state directly
    setBeneficiaryStep(value: number) {
      this.beneficiaryStep = value;
    },
    // TODO: verify if used
    setBeneficiaryPrimary(value) {
      this.beneficiaryPrimary = value;
    },
    // TODO: verify if used
    setBeneficiaryPrimaryContingent(value) {
      this.beneficiaryPrimaryContingent = value;
    },
    // TODO: verify if used
    setBeneficiarySpouse(value) {
      this.beneficiarySpouse = value;
    },
    // TODO: verify if used
    setBeneficiarySpouseContingent(value) {
      this.beneficiarySpouseContingent = value;
    },
    // TODO: verify if used
    // setBeneficiaryDataDirty(value) {
    //   this.isBeneficiaryDataDirty = value;
    // },
    setPaymentStep(value: number) {
      this.paymentStep = value;
    },
    // TODO: verify if used
    setRouteNameToNavigateBack(value) {
      this.routeNameToNavigateBack = value;
    },
    setWithDrawalDateStep(value: number) {
      this.withDrawalDateStep = value;
    },
    setChangeBankAccountStep(value: number) {
      this.changeBankAccountStep = value;
    },
    setPayMethodChange(value: number) {
      this.payMethodChange = value;
    },
    resetBankInfo(value: string) {
      this.bankName = value;
    },
    resetState() {
      Object.assign(getDefaultState());
    },

    //  actions
    // TODO: verify if used; access state directly
    setToReady() {
      this.step = STEP.READY;
    },
    // TODO: verify if used; access state directly
    startAddAChild() {
      this.step = STEP.ADD_A_CHILD;
    },
    // Do not call loadPaymentInfo directly.  Instead call getPolicyOverview
    async loadPaymentInfo() {
      const globalStore = useGlobalStore();
      try {
        const startingSelectedPolicyIndex = this.selectedPolicyIndex || 0;
        this.setPaymentPanel(this.policies);
        this.setPaymentInfoLoading(true);
        let i = 0;
        // TODO: use Promise.all
        for (i; i < this.policies.length; i++) {
          // Following is being done to set the policy so that getPaymentInfo can fetch the data for right one
          if (this.policies[0].contractId !== '0') {
            this.selectedPolicyIndex = i;
            await this.getPaymentInfo(this.policies[i].contractId);
          }
        }
        // Once everything is done we go back to the policy that was being used.
        this.selectedPolicyIndex = startingSelectedPolicyIndex;
        // TODO: use router
        if (window.location.pathname.includes('paynow')) {
          this.setPanelValue(0);
        }
      } catch (e) {
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
      } finally {
        this.setPaymentInfoLoading(false);
      }
    },
    // TODO: verify if used
    async getPolicies() {
      const customerStore = useCustomerStore();
      const globalStore = useGlobalStore();
      try {
        const policyOverview = await bff.getPolicyOverview(customerStore.customerProfile.policyIds);
        this.setPolicies(policyOverview.policies ?? []);
        this.navHintDismissed = policyOverview.preferences?.navHint ?? false;
        this.hasTerminatedPolicies = policyOverview.hasTerminatedPolicies;
        this.terminatedPolicyAgent = policyOverview.terminatedPolicyAgent;
        // Get agents for policies
        if (policyOverview.policies && policyOverview.policies.length > 0) {
          const ids: string[] = [];
          policyOverview.policies.forEach((a) => {
            if (a.agentId && !globalStore.getAgent(a.agentId) && !ids.includes(a.agentId)) {
              ids.push(a.agentId);
            }
          });
          if (ids.length > 0) {
            const agents = await bff.getAgents(ids);
            globalStore.addAgents(agents);
          }
        }
      } catch (e) {
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
      }
    },
    // TODO: verify if used
    async getPolicyOverview(refresh = false) {
      const globalStore = useGlobalStore();
      if (this.policies.length !== 0 && this.policies[0]?.contractId !== '0' && !refresh) {
        await this.loadPaymentInfo();
        return;
      }
      try {
        await this.setPolicyInfoLoading(true);
        await this.getPolicies();
        if (this.policies.length !== 0 && this.policies[0]?.contractId !== '0') {
          await this.loadPaymentInfo();
          if (this.policies[this.selectedPolicyIndex]?.kubraPaymentStatus) {
            checkKubraPayment();
          }
        }
      } catch (e) {
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
      } finally {
        this.setPolicyInfoLoading(false);
      }
    },
    // TODO: verify if used
    changePolicy({ selectedIndex }: { selectedIndex: number }) {
      const globalStore = useGlobalStore();
      if (selectedIndex >= 0 && selectedIndex < this.policies.length) {
        this.selectedPolicyIndex = selectedIndex;
        this.setDisablePaymentDetailsLink(false);
        if (this.policies[this.selectedPolicyIndex]) {
          this.getPaymentInfo(this.policies[this.selectedPolicyIndex].contractId);
        }
        // Remove the getAlerts for the payment
        globalStore.removeAlerts('payment');
      }
    },
    // TODO: verify if used
    nextPolicy() {
      let selectedIndex = this.selectedPolicyIndex + 1;

      while (selectedIndex < this.policies.length) {
        const policy = this.policies[selectedIndex];

        if (!policy.restricted && parseInt(policy.contractId, 10) !== 0) {
          this.changePolicy({ selectedIndex });
          return;
        }
        selectedIndex += 1;
      }
    },
    // TODO: verify if used
    prevPolicy() {
      let selectedIndex = this.selectedPolicyIndex - 1;

      while (selectedIndex >= 0) {
        const policy = this.policies[selectedIndex];

        if (!policy.restricted && parseInt(policy.contractId, 10) !== 0) {
          this.changePolicy({ selectedIndex });
          return;
        }
        selectedIndex -= 1;
      }
    },
    // TODO: verify if used
    getContactInfo(policy: IPolicyStorePolicy, contactInfo: IContactInfo) {
      return {
        policyNum: policy.contractId,
        phone: contactInfo.phone ?? policy.owner?.address.phone,
        email: contactInfo.email ?? policy.owner?.email,
        address: {
          address1: contactInfo.address1 ?? policy.owner?.address.address1,
          address2: contactInfo.address2 ?? policy.owner?.address.address2,
          city: contactInfo.city ?? policy.owner?.address.city,
          state: contactInfo.state ?? policy.owner?.address.state,
          zip: contactInfo.zip ?? policy.owner?.address.zip,
        },
      };
    },
    // TODO: verify if used
    getEmailInfo(policy: IPolicyStorePolicy, contactInfo: IContactInfo) {
      return {
        policyNum: policy.contractId,
        email: contactInfo.email ?? policy.owner?.email,
      };
    },
    // TODO: verify if used
    async verifyContactInfo(address: IContactInfo) {
      const policy = this.currentPolicy;
      try {
        const resp = await bff.verifyContactInfo(this.getContactInfo(policy, address));
        if (resp.data && !resp.data?.addressMatched) {
          this.addressVerification = resp.data;
          this.addressUpdateSuccess = false;
          return;
        }
      } catch (_e) {
        this.addressVerification = 'error';
        this.addressUpdateSuccess = false;
        return;
      }
      return this.updateContactInfo(address);
    },
    // TODO: verify if used
    async paymentSelection(type: PaymentSelectionType) {
      const policyNo = this.currentPolicy.contractId;
      const res = await bff.paymentSelection({ policyNo, type });
      return res;
    },
    // TODO: verify if used
    async updateContactInfo(contactInfo: IContactInfo) {
      const globalStore = useGlobalStore();
      let bffResponse;
      let errorArray;
      try {
        // if (Object.prototype.hasOwnProperty.call(contactInfo, 'address1') || Object.prototype.hasOwnProperty.call(contactInfo, 'phone') || Object.prototype.hasOwnProperty.call(contactInfo, 'email')) {
        const contactObj = [];
        this.successResponseUpdateContactInfo = [];
        this.errorResponseUpdateContactInfo = [];
        if (contactInfo.updateAll) {
          for (const policy of this.policies) {
            contactObj.push(this.getContactInfo(policy, contactInfo));
          }
          errorArray = contactObj.map((policyObj) => policyObj.policyNum);
          bffResponse = await bff.updateContactInfo(contactObj);
          // contactObj = getContactInfoInArrayWithPolicy(policy, contactInfo);
        } else {
          contactObj.push(this.getContactInfo(this.currentPolicy, contactInfo));
          errorArray = contactObj.map((policyObj) => policyObj.policyNum);
          bffResponse = await bff.updateContactInfo(contactObj);
        }
        // }
        this.successResponseUpdateContactInfo = bffResponse.data.success;
        this.errorResponseUpdateContactInfo = bffResponse.data.error;
        this.addressUpdateSuccess = true;
        this.addressVerification = undefined;
        this.setContactInfo(contactInfo);
      } catch (e) {
        this.errorResponseUpdateContactInfo = errorArray;
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
      }
    },
    // TODO: verify if used
    setContactInfo(address: IContactInfo) {
      this.updateOwnerAddress1(address.address1);
      this.updateOwnerAddress2(address.address2);
      this.updateOwnerCity(address.city);
      this.updateOwnerState(address.state);
      this.updateOwnerZip(address.zip);
      this.updateOwnerPhone(address.phone);
      this.updateOwnerEmail(address.email);
    },
    // TODO: verify if used
    async addChildRider(_childRider: IAddChildRiderPayload) {
      return bff.addChildToRider(_childRider);
    },

    async getPaymentInfo(policyNumber: string) {
      const globalStore = useGlobalStore();
      try {
        const _paymentInfo = await bff.getPaymentInfo(policyNumber);
        this.setPaymentInfo(_paymentInfo);
      } catch (e) {
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
      }
    },
    // TODO: verify if used
    // ***
    // TODO: move this to a common store module based on usage, see globalStore
    /* async getOnePolicyDocument (data) {
      try {
        // TODO: consider renaming since it returns urls for mobile
        const base64String = await content.streamDocument(data); // this has separate implementations for web and mobile
        this.setBase64String(base64String);
      } catch (e) {
        console.error(e); // TODO: add DT custom error and remove console.error
        // TODO: revisit
        // this.globalStore.addError({ error: e, context: 'app' }, { root: true } as IGlobalError);
      }
    }, */
    // TODO: verify if used
    // ***
    // TODO: move this to a common store module based on usage, see globalStore
    /* async getAllPolicyDocuments ({ commit }, data) {
      try {
        // TODO: consider renaming since it returns urls for mobile
        const base64String = await content.streamDocument(data); // this has separate implementations for web and mobile
        this.setBase64String(base64String);
      } catch (e) {
        console.error(e); // TODO: add DT custom error and remove console.error
        // TODO: revisit
        // this.globalStore.addError({ error: e, context: 'app' }, { root: true } as IGlobalError);
      }
    }, */
    // TODO: verify if used
    async getBankInfo(data: IGetBankDataPayload): Promise<boolean> {
      const globalStore = useGlobalStore();
      try {
        const bankData = await bff.getBankData(data);
        this.bankName = bankData as string;
        return true;
      } catch (e) {
        globalStore.addError({ error: e, context: 'payment' } as IGlobalError);
        return false;
      }
    },
    // TODO: verify if used
    async submitAch({
      paymentInfo,
      policyNumber,
    }: {
      paymentInfo: IPolicyStorePaymentInfoWBankAccount;
      policyNumber: string;
    }) {
      const globalStore = useGlobalStore();
      try {
        const originalNextCycleDate = this.currentPolicy.paymentInfo?.nextCycleDate;
        const response = await bff.submitAchPayment({ paymentInfo, policyNumber });
        if (response.status === 200 || response.status === 201 || response.status === 204) {
          // This is being done because the backend is returning empty nextCycle
          // TODO Provide long term fix.
          const nextCycleInfo = { NextCycleDate: originalNextCycleDate };
          const newPaymentInfo = {
            ...response.data,
            ...nextCycleInfo,
          };
          this.setPaymentInfo(newPaymentInfo);
        }
        return true;
      } catch (e) {
        globalStore.addError({ error: e, context: 'payment' } as IGlobalError);
        return false;
      }
    },
    // TODO: verify if used
    async updateBillingMode({
      paymentInfo,
      policyNumber,
    }: {
      paymentInfo: IPolicyStorePaymentInfoWBankAccount;
      policyNumber: string;
    }) {
      const globalStore = useGlobalStore();
      try {
        const res = await bff.updateBillingMode({ paymentInfo, policyNumber });
        return res.status === 200 || res.status === 201;
      } catch (e) {
        globalStore.addError({ error: e, context: 'payment' } as IGlobalError);
        return false;
      }
    },
    // TODO: verify if used
    async getDraftDescription(data: { paidToDate: string; selectedMonthlyPaymentDay: string }) {
      const globalStore = useGlobalStore();
      try {
        const res = await bff.getDraftDescription(data);
        return res.data;
      } catch (e) {
        globalStore.addError({ error: e, context: 'payment' } as IGlobalError);
        return false;
      }
    },
    async getBeneficiaries(policyNumber: string) {
      const globalStore = useGlobalStore();
      try {
        const _beneficiaries = await bff.getBeneficiaries(policyNumber);
        // TODO: create a helper mapper
        // Map Beneficiaries Data
        this.beneficiaries = _beneficiaries.map((d) => {
          const r: IPolicyStoreBeneficiaryInfo = {
            ...d,
            beneficiaryPercent: parseFloat(d.beneficiaryPercent),
          };
          return r;
        });
        const P = _beneficiaries.filter(
          (obj: IGetBeneficiariesResponse) =>
            obj.beneficiaryType.valueOf() === 'P' && (obj.origin === 'A' || obj.origin === 'C')
        );
        this.beneficiaryPrimary = P;
        const C = _beneficiaries.filter(
          (obj: IGetBeneficiariesResponse) =>
            obj.beneficiaryType.valueOf() === 'C' && (obj.origin === 'A' || obj.origin === 'C')
        );
        this.beneficiaryPrimaryContingent = C;
        const S = _beneficiaries.filter(
          (obj: IGetBeneficiariesResponse) =>
            obj.beneficiaryType.valueOf() === 'S' && (obj.origin === 'A' || obj.origin === 'C')
        );
        this.beneficiarySpouse = S;
        const H = _beneficiaries.filter(
          (obj: IGetBeneficiariesResponse) =>
            obj.beneficiaryType.valueOf() === 'H' && (obj.origin === 'A' || obj.origin === 'C')
        );
        this.beneficiarySpouseContingent = H;
      } catch (e) {
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
      }
    },
    async setBeneficiaries() {
      const globalStore = useGlobalStore();
      try {
        const finalPayload = [];
        finalPayload.push(...this.beneficiaryPrimary);
        finalPayload.push(...this.beneficiaryPrimaryContingent);
        finalPayload.push(...this.beneficiarySpouse);
        finalPayload.push(...this.beneficiarySpouseContingent);
        const response = await bff.setBeneficiaries(this.currentPolicy.contractId, finalPayload);
        return response;
      } catch (e) {
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
        return false;
      }
    },
    // TODO: verify if used
    async setMonthlyWithDrawalDay(day: string) {
      const globalStore = useGlobalStore();
      try {
        const response = await bff.setMonthlyWithDrawalDay(this.currentPolicy.contractId, day);
        return response;
      } catch (e) {
        globalStore.addError({ error: e, content: 'app' } as IGlobalError);
        return false;
      }
    },
    // TODO: verify if used
    /*
      -1 not set
      1 Show My Benefits page
      2 Show add/edit Beneficiary in Add mode
      3 Show add/edit Beneficiary in Edit mode
    */
  },
});
